import type { AssetCard } from '@/lib/api/types';
import { clampAspect, type GridItemBase } from './layout';

/** §3.6 optimistic placeholder card living in the board-assets query cache next to AssetCards. */
export type PlaceholderStatus = 'pending' | 'hashing' | 'initiating' | 'uploading' | 'completing' | 'processing' | 'ready' | 'failed' | 'paused' | 'cancelled' | 'duplicate';

export type PlaceholderCard = {
  __placeholder: true;
  id: string; // === client_upload_id
  client_upload_id: string;
  title: string;
  aspect_ratio: number;
  preview_url: string | null;
  status: PlaceholderStatus;
  /** 0..1 */
  progress: number;
  error: string | null;
  retryable: boolean;
  type: 'image' | 'video' | 'audio' | 'document' | 'other';
  size_bytes: number;
  asset_id: string | null;
  updated_at: string;
  color?: string;
};

export type BoardCardEntry = AssetCard | PlaceholderCard;

export function isPlaceholder(c: BoardCardEntry | null | undefined): c is PlaceholderCard {
  return !!c && (c as PlaceholderCard).__placeholder === true;
}

export type GridItem = GridItemBase & { card: BoardCardEntry };

/** Build grid items; stable object identity per card object (memoised by the caller). */
export function toGridItem(card: BoardCardEntry): GridItem {
  return { key: card.id, aspect: clampAspect(card.aspect_ratio ?? 1), card };
}

const itemCache = new WeakMap<BoardCardEntry, GridItem>();
/** Reference-stable conversion so an unchanged card yields the same GridItem (enables append detection). */
export function toGridItems(cards: readonly BoardCardEntry[]): GridItem[] {
  const out: GridItem[] = new Array(cards.length);
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i]!;
    let it = itemCache.get(c);
    if (!it) {
      it = toGridItem(c);
      itemCache.set(c, it);
    }
    out[i] = it;
  }
  return out;
}
